<script setup lang="ts">
    import type { GeometryData, IntUnits, IntUnitsSpecial } from '~/@types/cms';

    interface BikeGeometryProps {
        bike: string;
        data: GeometryData;
    }
    const props = defineProps<BikeGeometryProps>();

    const appStore = useAppStore();
    const { isoLocale } = useLocales();
    const { metric, imperial } = storeToRefs(appStore);

    const hover = ref('');
    const selected = ref('');
    const { t } = useLocales();

    const showClass = computed(() => {
        if (hover.value) {
            return hover.value;
        } else if (selected.value) {
            return selected.value;
        } else {
            return '';
        }
    });

    type GeometryDataLine = {
        label: string;
        type: string;
        data:
            | {
                  code: string;
                  value: string;
                  valueMax?: never;
              }
            | {
                  code: 'size';
                  value?: IntUnits;
                  valueMax?: IntUnits;
              }
            | {
                  code: 'mixed';
                  value?: IntUnitsSpecial;
                  valueMax?: never;
              };
    };
    const geometryData = computed<GeometryDataLine[] | undefined>(() => {
        if (!props.data) return;
        return [
            {
                label: 'wheel.size',
                type: 'tyres',
                data: {
                    code: 'wheelsize',
                    value: `${props.data.wheels}&#x2033;`,
                },
            },
            {
                label: 'handlebar.height',
                type: 'cockpit',
                data: {
                    code: 'size',
                    value: props.data.handlebar_height,
                    valueMax: props.data.handlebar_height_max,
                },
            },
            {
                label: 'steering.angle',
                type: 'steering-angle',
                data: {
                    code: 'angle',
                    value: `${props.data.steering_angle}&#xba;`,
                },
            },
            {
                label: 'topTube.horizontal',
                type: 'top-tube-horizontal',
                data: {
                    code: 'size',
                    value: props.data.top_tube,
                },
            },
            {
                label: 'wheel.base',
                type: 'wheelbase',
                data: {
                    code: 'size',
                    value: props.data.wheelbase,
                },
            },
            {
                label: 'seatTube.angle',
                type: 'seat-tube-angle',
                data: {
                    code: 'angle',
                    value: `${props.data.seat_tube_angle}&#xba;`,
                },
            },
            {
                label: 'saddle.height.min',
                type: 'min-saddle-height',
                data: {
                    code: 'size',
                    value: props.data.min_saddle_height,
                },
            },
            {
                label: 'saddle.height.max',
                type: 'max-saddle-height',
                data: {
                    code: 'size',
                    value: props.data.max_saddle_height,
                },
            },
            {
                label: 'crank.length',
                type: 'crank-length',
                data: {
                    code: 'size',
                    value: props.data.crank_length,
                },
            },
            {
                label: 'handlebar.adjust',
                type: 'handlebar-adjustability',
                data: {
                    code: 'mixed',
                    value: props.data.handlebar_adjust,
                },
            },
            {
                label: 'standover.height',
                type: 'stand-over-height',
                data: {
                    code: 'size',
                    value: props.data.standover,
                },
            },
        ];
    });

    const hoverState = (type: string) => {
        hover.value = type;
    };
    const selectedState = (type: string) => {
        if (selected.value !== type) {
            selected.value = type;
        } else {
            selected.value = '';
        }
    };

    type LineData = {
        label: string;
        type: string;
        value: string;
    };

    const toMetric = (value: number) =>
        new Intl.NumberFormat(isoLocale.value, {
            style: 'unit',
            unit: 'millimeter',
            unitDisplay: 'narrow',
        }).format(value);

    const toImperial = (value: number) =>
        new Intl.NumberFormat(isoLocale.value, {
            style: 'unit',
            unit: 'inch',
            unitDisplay: 'narrow',
        }).format(value);

    const getValueFromGeometryLine = ({ value, valueMax, code }: GeometryDataLine['data']) => {
        if (!value) return;

        if (typeof value === 'string' || typeof value === 'number') return value.toString();

        if (code === 'mixed') {
            if (!value.metric && !value.imperial) return value.text;

            const baseString = `${value.text} <br/>${t('bikeGeometry.height')} +/- `;
            if (metric.value && imperial.value && value.metric && value.imperial) {
                return `${baseString}${toMetric(value.metric)} (${toImperial(value.imperial)})`;
            }
            if (metric.value && value.metric) {
                return `${baseString}${toMetric(value.metric)}`;
            }
            if (imperial.value && value.imperial) {
                return `${baseString}${toImperial(value.imperial)}`;
            }
        }

        if (code === 'size') {
            const maxMetric = toMetric(valueMax?.metric || 0);
            const maxImperial = toImperial(valueMax?.imperial || 0);
            if (metric.value && imperial.value && value.metric && value.imperial) {
                const maxContent = valueMax ? ` - ${maxMetric} (${maxImperial})` : '';
                return `${toMetric(value.metric)} (${toImperial(value.imperial)})${maxContent}`;
            }
            if (metric.value && value.metric) {
                const maxContent = valueMax ? ` - ${maxMetric}` : '';
                return `${toMetric(value.metric)}${maxContent}`;
            }
            if (imperial.value && value.imperial) {
                const maxContent = valueMax ? ` - ${maxImperial}` : '';
                return `${toImperial(value.imperial)}${maxContent}`;
            }
        }
    };
    const geometryLines = computed(() => {
        if (!geometryData.value) return;
        return geometryData.value.reduce((currentValues, { data, label, type }) => {
            const lineValue = getValueFromGeometryLine(data);
            if (lineValue) {
                currentValues.push({
                    label,
                    type,
                    value: lineValue,
                });
            }

            return currentValues;
        }, [] as LineData[]);
    });
</script>
<template>
    <atm-grid
        full-width
        no-margin
        class="mol-bike-geometry"
        :class="showClass">
        <component
            :is="props.bike"
            class="col-span-2 md:col-span-12 lg:col-span-8" />
        <div class="col-span-2 flex md:col-span-12 lg:col-span-4">
            <div class="flex w-full flex-col gap-3 lg:justify-center">
                <template
                    v-for="(line, idx) in geometryLines"
                    :key="line.label">
                    <atm-geometry-line
                        :index="idx"
                        :label="$t('bikeGeometry.' + line.label)"
                        :value="line.value"
                        :type="line.type"
                        :selected="selected === line.type"
                        @hover="hoverState"
                        @click="selectedState" />
                </template>
            </div>
        </div>
    </atm-grid>
</template>

<style scoped>
    .mol-bike-geometry :deep(.h-line),
    .mol-bike-geometry :deep(.h-line-miter) {
        stroke-linecap: round;
        stroke-linejoin: round;
    }
    .mol-bike-geometry :deep(.h-line-miter) {
        stroke-miterlimit: 10;
    }
    .mol-bike-geometry :deep(.h-dash-line) {
        stroke-dasharray: 9.5669, 9.5669;
    }

    .mol-bike-geometry.tyres :deep(.tyres),
    .mol-bike-geometry.steering-angle :deep(.steering-angle),
    .mol-bike-geometry.cockpit :deep(.cockpit),
    .mol-bike-geometry.top-tube-horizontal :deep(.top-tube-horizontal),
    .mol-bike-geometry.wheelbase :deep(.wheelbase),
    .mol-bike-geometry.seat-tube-angle :deep(.seat-tube-angle),
    .mol-bike-geometry.min-saddle-height :deep(.min-saddle-height),
    .mol-bike-geometry.max-saddle-height :deep(.max-saddle-height),
    .mol-bike-geometry.crank-length :deep(.crank-length),
    .mol-bike-geometry.handlebar-adjustability :deep(.handlebar-adjustability),
    .mol-bike-geometry.stand-over-height :deep(.stand-over-height) {
        @apply inline;
    }

    .mol-bike-geometry.min-saddle-height :deep(.saddle-normal),
    .mol-bike-geometry.max-saddle-height :deep(.saddle-normal),
    .mol-bike-geometry.handlebar-adjustability :deep(.handlebar-normal) {
        @apply hidden;
    }
</style>
